import React, { forwardRef, useEffect, useRef, useState } from 'react';
import Modal, { ModalRef } from '@/components/@IntranetLibrary/Modal';
import IframeApproval, { IframeApprovalMessageAction } from './IframeApproval';
import ApprovalView from '@/components/Approval/ApprovalView';
import NextApprovalConfirmModal from '@/components/Approval/NextApprovalConfirmModal';
import LoadingLottie from '@/components/LoadingLottie';
import UnsavedCommentConfirmModal from '@/components/Approval/ApprovalView/UnsavedCommentConfirmModal';
import useNextApprovalDoc, {
  resolveApprovalDoc,
} from '@/components/Approval/@hooks/useNextApprovalDoc';
import type { ApprovalDecisionAction } from '@/hooks/approval/useApprovalDecision';
import { isNativeViewApprovalType } from '@/utils/approval/approvalAppTab';
import { getIntranetDocUrl } from '@/utils/urls';

type IframeApprovalModalProps = {
  url: string;
  dataItemChange?: (id: string, changes: Record<string, any>) => void;
  handleClose: () => void;
};

/** 호출부가 완성된 레거시 URL 만 넘겨서, 양식 판정에 필요한 문서 id 는 여기서 되뽑음 */
const getDocIdFromUrl = (url: string) => {
  try {
    return new URL(url).searchParams.get('doc') || undefined;
  } catch {
    return undefined;
  }
};

type DocBodyProps = {
  docId?: string;
  legacyUrl: string;
  needCloseMessage: boolean;
  onMessage: (action: IframeApprovalMessageAction, data: any) => void;
  /** 프로그램상 닫기(결재 후 다음 문서 없음, 레이어 닫기) — 가드 없음 */
  onClose: () => void;
  /** 사용자가 누른 닫기 — 작성 중 코멘트 가드를 태움 */
  onRequestClose: () => void;
  onOpenNextDoc: (docId: string) => void;
  dataItemChange?: (id: string, changes: Record<string, any>) => void;
  /** 작성 중 코멘트 이탈 가드 — 닫기 전에 호스트가 확인 */
  commentDirtyRef: React.MutableRefObject<(() => boolean) | null>;
};

/**
 * 양식을 판정해 V2 는 포탈 조회 화면, 그 외는 레거시 iframe.
 *
 * 모달이 열릴 때만 마운트되는 자리 — 판정과 프리페치를 여기서 해야 닫힌 채로 요청이 나가지 않음.
 */
const ApprovalDocBody = ({
  docId,
  legacyUrl,
  needCloseMessage,
  onMessage,
  onClose,
  onRequestClose,
  onOpenNextDoc,
  dataItemChange,
  commentDirtyRef,
}: DocBodyProps) => {
  const [typeCode, setTypeCode] = useState<number>();
  // 판정 전에 iframe 을 띄우면 V2 도 레거시 화면이 잠깐 보임
  const [isResolving, setIsResolving] = useState(true);

  useEffect(() => {
    if (!docId) {
      setIsResolving(false); // 판정 불가 — 기존대로 레거시
      return;
    }

    let isCurrent = true;
    setIsResolving(true);

    void resolveApprovalDoc(docId).then((doc) => {
      if (!isCurrent) return;
      // 조회 실패(typeCode undefined)도 레거시로 — 기존 동작 유지
      setTypeCode(doc.typeCode);
      setIsResolving(false);
    });

    return () => {
      isCurrent = false;
    };
  }, [docId]);

  const isNativeType = isNativeViewApprovalType(typeCode);

  // 레거시 양식은 다음 문서 선정이 레거시 몫 — 포탈이 고르면 큐가 어긋남
  const { nextDoc, promptNext, clearNextDoc } = useNextApprovalDoc(
    docId ?? null,
    isNativeType,
  );

  const handleDecided = async (action: ApprovalDecisionAction) => {
    // 보류(H)는 결재 큐에 남아 본인이 재처리 — 화면 유지
    if (action === 'hold' || !docId) return;
    dataItemChange?.(docId, { status: action === 'reject' ? 'R' : 'C' });
    const { next, stale } = await promptNext(docId, action);
    if (!next && !stale) onClose();
  };

  if (isResolving) return <LoadingLottie opacity={1} loop={true} />;

  if (!isNativeType || !docId) {
    return (
      <IframeApproval
        url={legacyUrl}
        needCloseMessage={needCloseMessage}
        onMessage={onMessage}
      />
    );
  }

  return (
    <>
      <ApprovalView
        documentId={docId}
        apvType={typeCode}
        onClose={onRequestClose}
        onStatusChange={(status) => dataItemChange?.(docId, { status })}
        onDecided={handleDecided}
        commentDirtyRef={commentDirtyRef}
      />
      <NextApprovalConfirmModal
        open={!!nextDoc}
        onOpenNext={() => {
          if (!nextDoc) return;
          // 모달은 닫지 않음 — 표시 문서만 교체, 양식 판정은 위 effect 가 다시 함
          onOpenNextDoc(nextDoc.docId);
          clearNextDoc();
        }}
        onClose={() => {
          clearNextDoc();
          onClose();
        }}
      />
    </>
  );
};

const IframeApprovalModal = forwardRef<ModalRef, IframeApprovalModalProps>(
  (props, ref) => {
    const { url, dataItemChange, handleClose } = props;
    const [needCloseMessage, setNeedCloseMessage] = useState(false);
    const commentDirtyRef = useRef<(() => boolean) | null>(null);
    const [showUnsavedConfirm, setShowUnsavedConfirm] = useState(false);
    /*
     * "다음 결재 문서 열기"로 교체된 문서. url 은 부모가 쥐고 있어 여기서 덮어씀
     * — 부모가 새 문서를 열면(url 변경) 교체분은 버림.
     */
    const [nextDocId, setNextDocId] = useState<string>();
    // 같은 문서를 다시 열면 url 이 그대로라 effect 만으로는 초기화가 안 됨 — 열림 시점에도 비움
    useEffect(() => setNextDocId(undefined), [url]);

    const docId = nextDocId ?? getDocIdFromUrl(url);
    const legacyUrl = nextDocId ? getIntranetDocUrl(nextDocId, true) : url;

    const doClose = () => {
      setShowUnsavedConfirm(false);
      handleClose();
    };

    /** 작성 중 코멘트가 있으면 확인 후 닫기 */
    const handleCloseWithGuard = () => {
      if (commentDirtyRef.current?.()) {
        setShowUnsavedConfirm(true);
        return;
      }
      doClose();
    };

    const onMessage = (action: IframeApprovalMessageAction, data: any) => {
      switch (action) {
        case 'MODAL_CLOSE': {
          handleCloseWithGuard();
          break;
        }
        case 'APPROVAL_CONFIRM': {
          if ('status' in data && 'docid' in data) {
            dataItemChange?.(data.docid, { status: data.status });
          }
          break;
        }
        case 'NEXT_CONFIRM_DOCUMENT': {
          if ('docid' in data && data.docid) setNextDocId(String(data.docid));
          break;
        }
        case 'ENABLE_ALERT': {
          setNeedCloseMessage(true);
          break;
        }
        case 'DISABLE_ALERT': {
          setNeedCloseMessage(false);
          break;
        }
      }
    };

    return (
      <>
        <Modal
          ref={ref}
          title={'Approval'}
          isFixedHeight
          isExpandable
          availableAutoClose={false}
          onOpen={() => {
            setNextDocId(undefined);
            setShowUnsavedConfirm(false);
          }}
          handleClose={handleCloseWithGuard}
        >
          <ApprovalDocBody
            docId={docId}
            legacyUrl={legacyUrl}
            needCloseMessage={needCloseMessage}
            onMessage={onMessage}
            onClose={doClose}
            onRequestClose={handleCloseWithGuard}
            onOpenNextDoc={setNextDocId}
            dataItemChange={dataItemChange}
            commentDirtyRef={commentDirtyRef}
          />
        </Modal>
        {/* 작성 중 코멘트 확인 모달 */}
        <UnsavedCommentConfirmModal
          open={showUnsavedConfirm}
          onConfirmClose={doClose}
          onKeepEditing={() => setShowUnsavedConfirm(false)}
        />
      </>
    );
  },
);
IframeApprovalModal.displayName = 'IframeApprovalModal';

export default IframeApprovalModal;
